export type StripeTransactionStatus = 'open' | 'complete' | 'expired'
export type StripePaymentStatus = 'paid' | 'unpaid' | 'no_payment_required'
export type StripeRecurringInterval = 'day' | 'week' | 'month' | 'year'

const stripeRecurringIntervals = new Set<string>(['day', 'week', 'month', 'year'])

export function isStripeRecurringInterval(value: string | undefined | null): value is StripeRecurringInterval {
  return typeof value === 'string' && stripeRecurringIntervals.has(value)
}

/**
 * Everything `GET /stripeConsumer/sessions/:sessionId` returns. That route is unauthenticated —
 * a session id is the only credential — and sends this object verbatim, so this type is the whole
 * allowlist. Widening it publishes the new field to anyone holding a session id; in particular the
 * session's raw `metadata` also carries device identifiers.
 *
 * Shared with the callers rather than mirrored on each side so a change to one is a compile error
 * on the other. Everything is nullable: Stripe leaves fields unset depending on the mode a session
 * was created in, and sessions predating a field carry none of it.
 */
export interface StripeSessionResponse {
  status: StripeTransactionStatus | null
  payment_status: StripePaymentStatus
  customer_email: string | null
  amount_total: number | null
  currency: string | null
  subscription_id: string | null
  /** Raw tier names as recorded on the session; validate with `isUserTier` before reporting them. */
  previous_tier: string | null
  purchased_tier: string | null
  recurring_interval: StripeRecurringInterval | null
  recurring_interval_count: number | null
}

// https://docs.stripe.com/currencies#zero-decimal, minus UGX: that page also lists it under
// https://docs.stripe.com/currencies#special-cases, where Stripe takes and reports it in hundredths.
const zeroDecimalCurrencies = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf'
])

// https://docs.stripe.com/currencies#three-decimal
const threeDecimalCurrencies = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd'])

/**
 * Converts a Stripe amount to the currency's major unit. Stripe reports amounts in the smallest
 * unit, and that unit is a hundredth for most currencies but not all: ¥1000 arrives as `1000` and
 * 1.500 KWD as `1500`, so a blanket divide by 100 would report a hundredth of what a zero-decimal
 * customer paid and ten times what a three-decimal one did.
 */
export function stripeAmountToMajorUnits(amount: number, currency: string): number {
  const code = currency.toLowerCase()
  if (zeroDecimalCurrencies.has(code)) {
    return amount
  }
  return threeDecimalCurrencies.has(code) ? amount / 1000 : amount / 100
}
